import React, { createContext, useContext, useState, ReactNode } from 'react';
import { Caravan } from '@/types/caravan';

export interface CartItem {
  caravan: Caravan;
  startDate: Date | null;
  endDate: Date | null;
  passengers: number;
  days: number;
  totalPrice: number;
  pickUpPlace?: { label: string; price: number };
  turnaround?: { label: string; price: number };
  pickUpTime?: string;
  dropOffTime?: string;
}

interface CartContextType {
  items: CartItem[];
  addToCart: (item: CartItem) => void;
  removeFromCart: (caravanId: string) => void;
  clearCart: () => void;
  getTotalPrice: () => number;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

export const CartProvider = ({ children }: { children: ReactNode }) => {
  const [items, setItems] = useState<CartItem[]>([]);

  const addToCart = (item: CartItem) => {
    setItems(prev => {
      const existing = prev.find(i => i.caravan.id === item.caravan.id);
      if (existing) {
        return prev.map(i => i.caravan.id === item.caravan.id ? item : i);
      }
      return [...prev, item];
    });
  };

  const removeFromCart = (caravanId: string) => {
    setItems(prev => prev.filter(item => item.caravan.id !== caravanId));
  };

  const clearCart = () => {
    setItems([]);
  };

  const getTotalPrice = () => {
    return items.reduce((sum, item) => sum + item.totalPrice, 0);
  };

  return (
    <CartContext.Provider value={{ items, addToCart, removeFromCart, clearCart, getTotalPrice }}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within CartProvider');
  }
  return context;
};
